/**
 * V2 admin · Tenant detail · Automated lifecycle emails (COL-302 / COL-304).
 *
 * Documents the three automated move-in (welcome T-7 · details T-1 · day-of)
 * or move-out (confirmed · upcoming T-7 · day-of) emails for a booking, read
 * from the tenant_lifecycle_emails send-log written by the
 * tenant-lifecycle-email-tick edge function.
 *
 * Status per email: Sent (with date) · Scheduled (future) · Due (tick will
 * pick it up) · Overdue/Missed (send window passed without a send — the
 * admin alert this section exists for) · Failed (with error) · Skipped.
 * "Run now" triggers an immediate tick for this booking (admin JWT auth).
 *
 * Send windows mirror supabase/functions/_shared/lifecycle-emails.ts — keep
 * the two in sync.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { CollapsibleCard } from "./tenant-profile-ui";
import { supabase } from "@/integrations/supabase/client";
import { businessToday } from "@/lib/businessDate";
import { formatDate, formatDateTime } from "@/lib/v2-format";

type Flow = "move_in" | "move_out";

type LogRow = {
  email_type: string;
  status: "pending" | "sent" | "failed" | "skipped";
  scheduled_for: string | null;
  sent_at: string | null;
  attempts: number;
  error: string | null;
  recipient: string | null;
};

type EmailSpec = {
  type: string;
  label: string;
  desc: string;
  /** Days relative to the anchor date; null = "immediately on confirmation". */
  offset: number | null;
  /** Last day (offset from anchor) the tick still sends — after this an
   * unsent email counts as missed. Mirrors LIFECYCLE_EMAIL_WINDOWS. */
  lastValid: number;
};

const FLOW_SPECS: Record<Flow, EmailSpec[]> = {
  move_in: [
    {
      type: "move_in_welcome",
      label: "Welcome email",
      desc: "T-7 · welcome & what happens next",
      offset: -7,
      lastValid: -2,
    },
    {
      type: "move_in_details",
      label: "Move-in details & key pickup",
      desc: "T-1 · key pickup, dashboard link, document attachments",
      offset: -1,
      lastValid: 1,
    },
    {
      type: "move_in_day",
      label: "Move-in day reminder",
      desc: "T-0 · move-in protocol due within 48h",
      offset: 0,
      lastValid: 1,
    },
  ],
  move_out: [
    {
      type: "move_out_confirmed",
      label: "Move-out confirmed",
      desc: "Immediately once the move-out date is confirmed",
      offset: null,
      lastValid: 0,
    },
    {
      type: "move_out_upcoming",
      label: "Move-out coming up",
      desc: "T-7 · handover protocol & key return location",
      offset: -7,
      lastValid: -1,
    },
    {
      type: "move_out_day",
      label: "Move-out day",
      desc: "T-0 · final reminders",
      offset: 0,
      lastValid: 1,
    },
  ],
};

function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return t.toISOString().slice(0, 10);
}

/** Shared with the rent tab — see businessDate.ts for why this is not UTC. */
const todayIso = businessToday;

/** Automation ship date — flows whose whole send window predates it never
 * had a chance to send, so they render as "—" instead of a red "Missed"
 * on every legacy tenant. */
const AUTOMATION_LIVE_FROM = "2026-08-01";

type RowState = {
  chip: string;
  chipStyle: React.CSSProperties;
  detail: string;
  alert: boolean;
};

const CHIP_BASE: React.CSSProperties = {
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 999,
  fontSize: 11.5,
  fontWeight: 700,
};
const CHIPS = {
  green: { ...CHIP_BASE, background: "#E6F7EF", color: "#0B7268" },
  slate: { ...CHIP_BASE, background: "#F1F3F6", color: "#64748B" },
  amber: { ...CHIP_BASE, background: "#FFF6E6", color: "#B45309" },
  red: { ...CHIP_BASE, background: "#FEECEC", color: "#B91C1C" },
};

function rowState(args: {
  spec: EmailSpec;
  row: LogRow | undefined;
  anchor: string | null;
  active: boolean;
}): RowState {
  const { spec, row, anchor, active } = args;

  if (row?.status === "sent") {
    return {
      chip: "Sent",
      chipStyle: CHIPS.green,
      detail: row.sent_at ? formatDateTime(row.sent_at) : "Sent",
      alert: false,
    };
  }
  if (row?.status === "failed") {
    return {
      chip: "Failed",
      chipStyle: CHIPS.red,
      detail: `${row.attempts}× tried${row.error ? ` — ${row.error.slice(0, 120)}` : ""}`,
      alert: true,
    };
  }
  if (row?.status === "skipped") {
    return {
      chip: "Skipped",
      chipStyle: CHIPS.slate,
      detail: row.error ?? "Skipped",
      alert: false,
    };
  }

  if (!active || !anchor) {
    return {
      chip: "—",
      chipStyle: CHIPS.slate,
      detail:
        args.spec.offset === null
          ? "Sends when the date is confirmed"
          : "Not scheduled yet",
      alert: false,
    };
  }

  const today = todayIso();
  const planned =
    spec.offset === null ? today : addDaysIso(anchor, spec.offset);
  const lastValid = addDaysIso(anchor, spec.lastValid);

  if (lastValid < AUTOMATION_LIVE_FROM) {
    return {
      chip: "—",
      chipStyle: CHIPS.slate,
      detail: "Predates email automation",
      alert: false,
    };
  }
  if (today > lastValid) {
    return {
      chip: "Missed",
      chipStyle: CHIPS.red,
      detail: `Was due ${formatDate(planned)} — send window passed`,
      alert: true,
    };
  }
  if (today >= planned) {
    return {
      chip: row?.status === "pending" ? "Sending…" : "Due",
      chipStyle: CHIPS.amber,
      detail: `Due ${formatDate(planned)} — next automated run sends it`,
      alert: false,
    };
  }
  return {
    chip: "Scheduled",
    chipStyle: CHIPS.slate,
    detail: `Planned for ${formatDate(planned)}`,
    alert: false,
  };
}

export function LifecycleEmailsSection({
  bookingId,
  flow,
  anchorDate,
  active,
}: {
  bookingId: number | null;
  flow: Flow;
  /** move_in_date (move-in flow) or the confirmed move_out_date. */
  anchorDate: string | null;
  /** Whether the flow is armed (move-in: booking confirmed; move-out: date
   * confirmed). Inactive flows render every row as "Not scheduled". */
  active: boolean;
}) {
  const queryClient = useQueryClient();
  const specs = FLOW_SPECS[flow];
  const [open, setOpen] = useState(false);

  const { data: rows } = useQuery<LogRow[]>({
    queryKey: ["v2", "lifecycle-emails", bookingId, flow],
    enabled: !!bookingId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenant_lifecycle_emails")
        .select(
          "email_type, status, scheduled_for, sent_at, attempts, error, recipient"
        )
        .eq("booking_id", bookingId!);
      if (error) throw error;
      return (data ?? []) as LogRow[];
    },
  });

  const byType = useMemo(
    () => new Map((rows ?? []).map((r) => [r.email_type, r])),
    [rows]
  );

  const states = specs.map((spec) => ({
    spec,
    state: rowState({
      spec,
      row: byType.get(spec.type),
      anchor: anchorDate ? anchorDate.slice(0, 10) : null,
      active,
    }),
  }));
  const alertCount = states.filter((s) => s.state.alert).length;

  const runNow = useMutation({
    mutationFn: async () => {
      if (!bookingId) throw new Error("No booking id");
      const { data, error } = await supabase.functions.invoke(
        "tenant-lifecycle-email-tick",
        { body: { bookingId } }
      );
      if (error) throw error;
      if (data && data.ok === false) {
        throw new Error(data.error ?? "Run failed");
      }
    },
    onSuccess: () => {
      toast.success("Lifecycle email run triggered");
      queryClient.invalidateQueries({
        queryKey: ["v2", "lifecycle-emails", bookingId, flow],
      });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Run failed");
    },
  });

  return (
    <CollapsibleCard
      iconBg="#EFF6FF"
      iconColor="#2563EB"
      icon={<Mail size={19} />}
      title={flow === "move_in" ? "Move-in emails" : "Move-out emails"}
      subtitle="Automated · sent from info@coliviq.de (German + English)"
      right={
        alertCount > 0 ? (
          <span style={CHIPS.red}>
            {alertCount} {alertCount === 1 ? "issue" : "issues"}
          </span>
        ) : undefined
      }
      action={
        <button
          onClick={() => runNow.mutate()}
          disabled={!bookingId || runNow.isPending}
          title="Trigger an immediate send run for this booking (only due, unsent emails go out)"
          style={{
            border: "1px solid #E7E9EC",
            background: "#fff",
            color: "#0B7268",
            borderRadius: 10,
            padding: "7px 12px",
            fontSize: 12.5,
            fontWeight: 700,
            cursor: runNow.isPending ? "default" : "pointer",
            opacity: runNow.isPending ? 0.6 : 1,
          }}
        >
          {runNow.isPending ? "Running…" : "Run now"}
        </button>
      }
      open={open}
      onToggle={() => setOpen((v) => !v)}
    >
      {states.map(({ spec, state }, i) => (
        <div
          key={spec.type}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "13px 24px",
            borderBottom:
              i === states.length - 1 ? "none" : "1px solid #F5F6F7",
            background: state.alert ? "#FFF9F9" : "#fff",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{spec.label}</div>
            <div
              style={{
                fontSize: 12,
                color: "#94A3B8",
                fontWeight: 500,
                marginTop: 1,
              }}
            >
              {spec.desc}
            </div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0, maxWidth: 260 }}>
            <span style={state.chipStyle}>{state.chip}</span>
            <div
              style={{
                fontSize: 11,
                color: state.alert ? "#B91C1C" : "#94A3B8",
                fontWeight: 500,
                marginTop: 3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={state.detail}
            >
              {state.detail}
            </div>
          </div>
        </div>
      ))}
    </CollapsibleCard>
  );
}
